import React from "react";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { useRoomUsageAnalytics } from "@/hooks/useRooms";
import type { DateRange } from "@/hooks/useDateRange";
import { toRoomUsageApiRange } from "@/lib/roomUsage";
import { RoomsUsagePerformanceCard } from "./RoomsUsagePerformanceCard";

interface RoomsAnalyticsOverviewProps {
  hotelId: string;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

export function RoomsAnalyticsOverview({
  hotelId,
  dateRange,
  onDateRangeChange,
}: RoomsAnalyticsOverviewProps) {
  const { t } = useTranslation();
  const apiRange = React.useMemo(
    () => toRoomUsageApiRange(dateRange),
    [dateRange],
  );

  const analyticsQuery = useRoomUsageAnalytics(hotelId, {
    ...apiRange,
    groupBy: "room",
  });
  const analytics = analyticsQuery.data;

  return (
    <section className="border-border bg-card w-full rounded-2xl border p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold">
            {t("reservation.roomUsage.topRoomsTitle")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("reservation.roomUsage.topRoomsSubtitle")}
          </p>
        </div>

        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          triggerVariant="new_outline"
        />
      </div>

      <RoomsUsagePerformanceCard
        items={analytics?.items ?? []}
        isLoading={analyticsQuery.isLoading}
      />
    </section>
  );
}
